import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import type * as React from 'react'

import { cn } from '@/lib/utils'

const badgeVariants = cva(
	'inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-body font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-(--icon-standalone) gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid-style transition-[color,box-shadow] overflow-hidden',
	{
		variants: {
			variant: {
				default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
				secondary: 'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
				destructive:
					'border-transparent bg-destructive text-destructive-foreground [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20',
				success:
					'border-transparent bg-success text-success-foreground [a&]:hover:bg-success/90 focus-visible:ring-destructive/20',
				outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
				info: 'bg-info-bg text-info border-info/20 [a&]:hover:bg-info-bg/90',
				warning:
					'bg-status-warning-bg text-status-warning-text border-status-warning-text/20 [a&]:hover:bg-status-warning-bg/90',
				critical:
					'bg-status-danger-bg text-status-danger-text border-status-danger-text/20 [a&]:hover:bg-status-danger-bg/90',
				'status-success':
					'bg-status-success-bg text-status-success-text border-status-success-text/20 [a&]:hover:bg-status-success-bg/90',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
)

function Badge({
	className,
	variant,
	asChild = false,
	...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
	const Comp = asChild ? Slot : 'span'

	return <Comp data-slot='badge' className={cn(badgeVariants({ variant }), className)} {...props} />
}

export { Badge, badgeVariants }
